import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";
import TenantUserCreateForm from "components/TenantUserCreateForm";
import { updateTenantUser, setTenantUserStatus, deleteTenantUser } from "services/tenantUserAdminService";
import { useAuthz } from "context/AuthzContext";
import { TC } from "../tokens/text";

const DIALOG_PAPER_SX = {
  backgroundColor: "#ffffff",
  color: TC.primary,
  border: "1px solid #d8e6f7",
  borderRadius: "12px",
};

function SectionLabel({ children }) {
  return (
    <VuiTypography
      variant="caption" fontWeight="bold"
      sx={{ color: TC.primary, textTransform: "uppercase", letterSpacing: "0.08em", mt: 1, display: "block" }}
    >
      {children}
    </VuiTypography>
  );
}

function StatusBadge({ active }) {
  return (
    <VuiBadge
      variant="standard"
      badgeContent={active === false ? "Inactive" : "Active"}
      color={active === false ? "warning" : "success"}
      size="xs"
      container
      sx={({ palette }) => ({
        background: active === false ? "rgba(245,166,35,0.85)" : palette.success.main,
        borderRadius: 8,
        color: palette.white.main,
      })}
    />
  );
}

export default function TenantUserProfilePanel({ tenantId, selectedUser, onSaved }) {
  const [state, setState] = useState({ isSaving: false, error: "" });
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const isScimProvisioned = Boolean(
    selectedUser?.scimProvisioned ||
    selectedUser?.createdBy === "system:scim" ||
    (selectedUser?._ssoEntry && `${selectedUser._ssoEntry.createdBy || ""}`.toLowerCase().includes("scim"))
  );

  useEffect(() => {
    if (selectedUser) {
      setDisplayName(selectedUser.displayName || "");
      setRole(selectedUser.role || "user");
      setState((s) => ({ ...s, error: "" }));
    }
  }, [selectedUser]);

  if (!selectedUser) {
    return (
      <Card sx={{ border: "1px solid #e6eef7" }}>
        <VuiBox p={3}>
          <VuiTypography variant="button" sx={{ color: TC.primary }}>
            Select a user to view details
          </VuiTypography>
        </VuiBox>
      </Card>
    );
  }

  const handleSave = async () => {
    setState({ isSaving: true, error: "" });
    try {
      await updateTenantUser(tenantId, selectedUser.uid || selectedUser.id, { displayName, role });
      setState({ isSaving: false, error: "" });
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      setState({ isSaving: false, error: err?.message || "Failed to save user." });
    }
  };

  const handleToggleActive = async () => {
    setState((s) => ({ ...s, isSaving: true, error: "" }));
    try {
      await setTenantUserStatus({ tenantId, uid: selectedUser.uid || selectedUser.id, active: !(selectedUser.active !== false) });
      setState({ isSaving: false, error: "" });
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      setState({ isSaving: false, error: err?.message || "Failed to update status." });
    }
  };

  const handleDelete = async () => {
    setState((s) => ({ ...s, isSaving: true, error: "" }));
    try {
      await deleteTenantUser({ tenantId, uid: selectedUser.uid || selectedUser.id });
      setState({ isSaving: false, error: "" });
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      setState({ isSaving: false, error: err?.message || "Failed to delete user." });
    }
  };

  return (
    <Card sx={{ border: "1px solid #e6eef7" }}>
      <VuiBox p={3}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <VuiBox>
            <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>{selectedUser.displayName || selectedUser.email}</VuiTypography>
            <VuiBox display="flex" gap={1} mt={0.5} alignItems="center">
              <StatusBadge active={selectedUser.active} />
              {isScimProvisioned ? (
                <VuiBadge color="success">SSO Enabled</VuiBadge>
              ) : null}
            </VuiBox>
          </VuiBox>

          <VuiBox display="flex" alignItems="center" gap={2}>
            <VuiButton color="info" size="small" onClick={() => setCreateOpen(true)}>Create user</VuiButton>
          </VuiBox>
        </VuiBox>

        <VuiBox mt={2}>
          <Divider sx={{ my: 2, borderColor: "#d8e6f7" }} />

          <SectionLabel>Identity</SectionLabel>
          <VuiBox mt={1} p={1.2} sx={{ background: "#f4f8ff", borderRadius: 1, border: "1px solid #d8e6f7" }}>
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>UID</VuiTypography>
            <VuiInput value={selectedUser.uid || selectedUser.id || ""} readOnly sx={{ mt: 1 }} />

            <VuiTypography variant="caption" sx={{ color: TC.secondary, mt: 1 }}>Email</VuiTypography>
            <VuiInput value={selectedUser.email} readOnly sx={{ mt: 1 }} />

            <VuiTypography variant="caption" sx={{ color: TC.secondary, mt: 1 }}>Display name</VuiTypography>
            <VuiInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} sx={{ mt: 1 }} />

            <VuiTypography variant="caption" sx={{ color: TC.secondary, mt: 1 }}>Role</VuiTypography>
            <select value={role} onChange={(e) => setRole(e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 6 }}>
              <option value="user">user</option>
              <option value="tenant_admin">tenant_admin</option>
            </select>
          </VuiBox>
        </VuiBox>

        <VuiBox mt={2}>
          <Divider sx={{ my: 2, borderColor: "#d8e6f7" }} />

          <SectionLabel>Permissions</SectionLabel>
          <VuiBox mt={1} p={1} sx={{ background: "#fafcff", borderRadius: 1 }}>
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Permissions</VuiTypography>
            <VuiTypography variant="caption" sx={{ display: 'block', color: TC.primary }}>{(selectedUser.permissions || []).join(', ') || '—'}</VuiTypography>

            <VuiTypography variant="caption" sx={{ color: TC.secondary, mt: 1 }}>Assigned Roles</VuiTypography>
            <VuiTypography variant="caption" sx={{ display: 'block', color: TC.primary }}>{(selectedUser.assignedRoleIds || []).join(', ') || '—'}</VuiTypography>
          </VuiBox>
        </VuiBox>

        <VuiBox mt={2}>
          <Divider sx={{ my: 2, borderColor: "#d8e6f7" }} />

          <SectionLabel>Audit</SectionLabel>
          <VuiBox mt={1} display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1}>
            <VuiBox>
              <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Created</VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.primary }}>{selectedUser.createdAt || '—'}</VuiTypography>
            </VuiBox>
            <VuiBox>
              <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Last updated</VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.primary }}>{selectedUser.updatedAt || '—'}</VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>

        {state.error ? (
          <VuiTypography color="error" variant="button" mt={2} display="block">{state.error}</VuiTypography>
        ) : null}

        <VuiBox mt={3} display="flex" gap={2}>
          <VuiButton color="primary" onClick={handleSave} disabled={state.isSaving}>{state.isSaving ? 'Saving...' : 'Save'}</VuiButton>
          <VuiButton color={selectedUser.active === false ? 'success' : 'warning'} onClick={handleToggleActive} disabled={state.isSaving}>{selectedUser.active === false ? 'Reactivate' : 'Deactivate'}</VuiButton>
          <VuiButton color="error" onClick={() => setConfirmDelete(true)} disabled={state.isSaving}>Delete</VuiButton>
        </VuiBox>

        <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} aria-labelledby="confirm-delete" aria-describedby="confirm-delete-desc">
          <DialogTitle id="confirm-delete">Delete user</DialogTitle>
          <DialogContent>
            <VuiTypography id="confirm-delete-desc">Are you sure you want to permanently delete {selectedUser.displayName || selectedUser.email}? This cannot be undone.</VuiTypography>
          </DialogContent>
          <DialogActions>
            <VuiButton color="text" size="small" onClick={() => setConfirmDelete(false)}>Cancel</VuiButton>
            <VuiButton color="error" size="small" onClick={() => { setConfirmDelete(false); handleDelete(); }}>Delete</VuiButton>
          </DialogActions>
        </Dialog>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
          <DialogTitle sx={{ color: TC.primary }}>Create user</DialogTitle>
          <DialogContent dividers>
            <TenantUserCreateForm onUserChanged={() => { setCreateOpen(false); if (typeof onSaved === 'function') onSaved(); }} />
          </DialogContent>
          <DialogActions>
            <VuiButton color="text" size="small" onClick={() => setCreateOpen(false)}>Close</VuiButton>
          </DialogActions>
        </Dialog>
      </VuiBox>
    </Card>
  );
}
